/**
 * 用户端客服聊天 WebSocket 客户端
 * 协议与后端 internal/chat/ws 对应：
 *   上行：{"method":"chat.send","data":{...}} / chat.read / chat.typing / chat.logout
 *   下行：{"method":"chat.message|chat.ack|chat.read|chat.session|chat.unread|chat.error", "data":{...}}
 *   心跳：客户端每 5s 发 {"ping":ts}，服务端回 {"pong":ts}
 */
import Const from "@/utils/consts";

const RECONNECT_DELAY = 3000;
const PING_INTERVAL = 5000;

class ChatSocket {
  constructor() {
    this.socket = null;
    this.state = 0; // 0 未连接 / 1 已连接
    this.handlers = {}; // method -> [fn]
    this.pingTimer = null;
    this.reconnectTimer = null;
    this.manualClose = false;
    /** 是否已被服务端踢下线（token 失效）：同 token 不再重连 */
    this.kicked = false;
    /** 被踢时的 token，用于判断是否已重新登录 */
    this.kickedToken = "";
  }

  /** 拼接连接地址（开发环境直连本地聊天服务 8813，生产走同域 /ws） */
  buildUrl() {
    const token = localStorage.getItem(Const.KEY_TOKEN) || "";
    let base;
    if (process.env.NODE_ENV === "development") {
      base = "ws://127.0.0.1:8813/ws";
    } else {
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      base = `${proto}//${window.location.host}/ws`;
    }
    let lang = "en";
    try {
      const cached = JSON.parse(localStorage.getItem(Const.KEY_LANG) || "null");
      if (cached && cached.lang) {
        lang = String(cached.lang).slice(0, 2);
      }
    } catch (e) {
      lang = "en";
    }
    return `${base}?token=${encodeURIComponent(token)}&lang=${lang}`;
  }

  /** 注册事件回调 */
  on(method, fn) {
    if (!this.handlers[method]) this.handlers[method] = [];
    if (!this.handlers[method].includes(fn)) this.handlers[method].push(fn);
  }

  /** 取消事件回调 */
  off(method, fn) {
    if (!this.handlers[method]) return;
    if (!fn) {
      delete this.handlers[method];
      return;
    }
    this.handlers[method] = this.handlers[method].filter((cb) => cb !== fn);
  }

  emit(method, data) {
    (this.handlers[method] || []).forEach((fn) => {
      try {
        fn(data);
      } catch (e) {
        console.warn("[chat.socket] handler error:", method, e);
      }
    });
  }

  /** 建立连接（已连接则忽略） */
  open() {
    if (this.socket && (this.state === 1 || this.socket.readyState === 0)) {
      return;
    }
    const token = localStorage.getItem(Const.KEY_TOKEN) || "";
    if (!token) {
      // 未登录不建连（用户登录后由页面重新调用）
      return;
    }
    // 被踢下线且 token 未变化：不再重连（避免 401 死循环）；重新登录后 token 变化会自动恢复
    if (this.kicked && token === this.kickedToken) {
      return;
    }
    this.kicked = false;
    this.kickedToken = "";

    this.manualClose = false;
    let socket;
    try {
      socket = new WebSocket(this.buildUrl());
    } catch (e) {
      console.warn("[chat.socket] create failed:", e);
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.state = 1;
      this.emit("open");
      this.startPing();
    };

    socket.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      // 服务端心跳：{"type":"ping"} -> 回 {"pong":ts}
      if (data.type === "ping") {
        this.sendRaw(JSON.stringify({ pong: Date.now() }));
        return;
      }
      if (data.pong) return; // 我方心跳回执
      if (data.method === "chat.kick") {
        // 登录态失效（被顶下线 / token 过期）：记录失效时的 token 并停止重连，
        // 交给上层清理登录态并跳登录；重新登录后 token 变化会自动恢复连接
        this.kicked = true;
        this.kickedToken = localStorage.getItem(Const.KEY_TOKEN) || "";
        this.manualClose = true;
        this.stopReconnect();
        this.stopPing();
        this.emit("chat.kick", data.data);
        return;
      }
      if (data.method) {
        this.emit(data.method, data.data);
      }
    };

    socket.onerror = () => {
      this.emit("error");
    };

    socket.onclose = () => {
      this.state = 0;
      this.stopPing();
      this.emit("close");
      // 被踢下线时不再重连（上面已记录失效 token）
      if (!this.manualClose && !this.kicked) {
        this.scheduleReconnect();
      }
    };
  }

  /** 发送原始帧 */
  sendRaw(text) {
    if (this.socket && this.state === 1 && this.socket.readyState === 1) {
      this.socket.send(text);
      return true;
    }
    return false;
  }

  /** 按协议发送业务报文 */
  sendChat(method, data) {
    return this.sendRaw(JSON.stringify({ method, data: data || {} }));
  }

  /** 主动关闭 */
  close() {
    this.manualClose = true;
    this.stopPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      try {
        this.socket.close();
      } catch (e) {
        // ignore
      }
      this.socket = null;
    }
    this.state = 0;
  }

  /** 断线重连 */
  scheduleReconnect() {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, RECONNECT_DELAY);
  }

  /** 停止重连（被踢下线 / 主动关闭时用） */
  stopReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /** 心跳保活 */
  startPing() {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      this.sendRaw(JSON.stringify({ ping: Date.now() }));
    }, PING_INTERVAL);
  }

  stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  isConnected() {
    return this.state === 1;
  }
}

// 单例：整个用户端共用一个连接（与后端"同账号多端在线"策略一致）
const chatSocket = new ChatSocket();
export default chatSocket;
